/**
 * Role selection cards (Manager / OMR / Combined).
 *
 * Shared presentational component used by both the new-session "Identify Your
 * Role" screen and the resume flow, so the two look and behave identically.
 */

import React from 'react';
import Card, { CardRadioButton } from '@amzn/meridian/card';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { Role } from '../../../utils/profileUtils';

interface RoleSelectCardsProps {
  selectedRole: Role;
  onRoleChange: (role: Role) => void;
}

const ROLE_OPTIONS: Array<{ value: Role; label: string; description: string }> = [
  {
    value: 'whs_manager',
    label: 'Manager',
    description: 'Oversee overall site compliance, review incident trends, and manage administrative safety protocols.',
  },
  {
    value: 'omr',
    label: 'OMR',
    description: 'Operational Medical Representative focusing on immediate health response and specialized safety reporting.',
  },
  {
    value: 'combined',
    label: 'Combined: Manager & OMR',
    description: 'Capture both the incident details and the associate\'s medical condition in a single session.',
  },
];

const RoleSelectCards: React.FC<RoleSelectCardsProps> = ({ selectedRole, onRoleChange }) => (
  <div style={{ display: 'flex', gap: '16px' }}>
    {ROLE_OPTIONS.map((opt) => (
      <Card
        key={opt.value}
        spacingInset="none"
        checked={selectedRole === opt.value}
        onClick={(value: Role) => onRoleChange(value)}
        value={opt.value}
        width="fill"
      >
        <Row spacingInset="400" alignmentHorizontal="justify">
          <Text tag="label" type="b500" htmlFor={`role-radio-${opt.value}`}>
            {opt.label}
          </Text>
          <Column alignmentHorizontal="end">
            <CardRadioButton id={`role-radio-${opt.value}`} />
          </Column>
        </Row>
        <Column spacingInset="none 400 400 400">
          <Text type="b200">{opt.description}</Text>
        </Column>
      </Card>
    ))}
  </div>
);

export default RoleSelectCards;
